// Observational only: no identifiers or Firestore payloads enter this collector.
export const LESSON_LOAD_SLOW_MS = 10000;
const STAGES = ["COURSE_WAIT", "ENROLLMENT_WAIT", "PROGRESS_WAIT", "CURRICULUM_TOTAL",
  "CURRICULUM_HEADERS", "CURRICULUM_SECTIONS", "CURRICULUM_ITEMS", "TABLE_READY"];
const CODES = new Set(["cancelled", "unknown", "invalid-argument", "deadline-exceeded",
  "not-found", "already-exists", "permission-denied", "resource-exhausted",
  "failed-precondition", "aborted", "out-of-range", "unimplemented", "internal",
  "unavailable", "data-loss", "unauthenticated"]);
const noop = { begin: () => null, end: () => {}, finish: () => {}, ready: () => {}, abandon: () => {} };

export function createLessonLoadDiagnostic() {
  try {
    const crypto = window.crypto;
    const correlationId = typeof crypto.randomUUID === "function" ? crypto.randomUUID()
      : Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, "0")).join("");
    const started = performance.now();
    const stages = Object.fromEntries(STAGES.map(stage => [stage, {
      startedCount: 0, completedCount: 0, elapsedMs: 0, maxElapsedMs: 0,
      errorCount: 0, errorCode: undefined, firstStartedAtMs: null, lastCompletedAtMs: null,
    }]));
    const pending = new Set();
    const counts = { queryCount: 0, completedCount: 0, headerCount: 0, sectionCount: 0, itemCount: 0 };
    let closed = false;
    let timer;
    const safe = fn => (...args) => { try { return fn(...args); } catch { return undefined; } };
    const elapsed = from => Math.max(0, Math.round(performance.now() - from));
    const codeOf = error => {
      const code = typeof error?.code === "string" ? error.code.replace(/^firestore\//, "") : "unknown";
      return CODES.has(code) ? code : "unknown";
    };
    const emit = safe((outcome) => {
      const pendingStages = [...pending].map(token => ({ stage: token.stage,
        ordinal: token.ordinal, elapsedMs: elapsed(token.started) }));
      const currentStage = pendingStages.find(x => /^CURRICULUM_(HEADERS|SECTIONS|ITEMS)$/.test(x.stage))?.stage
        || pendingStages.find(x => x.stage !== "CURRICULUM_TOTAL")?.stage || "TABLE_READY";
      console.info(outcome === "slow" ? "COURSEVIEW_LESSON_LOAD_SLOW" : "COURSEVIEW_LESSON_LOAD", {
        correlationId, outcome, elapsedMs: elapsed(started), currentStage,
        ...counts, pendingStages,
        stages: Object.fromEntries(STAGES.map(stage => [stage, { ...stages[stage] }])),
      });
    });
    const begin = safe(stage => {
      if (closed || !STAGES.includes(stage)) return null;
      const token = { stage, started: performance.now(), ordinal: ++stages[stage].startedCount };
      if (stages[stage].firstStartedAtMs === null) stages[stage].firstStartedAtMs = elapsed(started);
      pending.add(token);
      if (/^CURRICULUM_(HEADERS|SECTIONS|ITEMS)$/.test(stage)) counts.queryCount++;
      return token;
    });
    const end = safe((token, error, resultCount = 0) => {
      if (closed || !pending.delete(token)) return;
      const row = stages[token.stage];
      const ms = elapsed(token.started);
      row.completedCount++;
      row.lastCompletedAtMs = elapsed(started);
      row.elapsedMs += ms;
      row.maxElapsedMs = Math.max(row.maxElapsedMs, ms);
      if (error) { row.errorCount++; row.errorCode = codeOf(error); }
      const countKey = { CURRICULUM_HEADERS: "headerCount", CURRICULUM_SECTIONS: "sectionCount", CURRICULUM_ITEMS: "itemCount" }[token.stage];
      if (countKey) {
        counts.completedCount++;
        if (!error && Number.isSafeInteger(resultCount) && resultCount >= 0) counts[countKey] += resultCount;
      }
    });
    const finish = safe(outcome => {
      if (closed) return;
      closed = true;
      clearTimeout(timer);
      emit(outcome);
      pending.clear();
    });
    timer = setTimeout(safe(() => { if (!closed) emit("slow"); }), LESSON_LOAD_SLOW_MS);
    return { begin, end, finish, abandon: () => finish("abandoned"), ready: safe(() => {
      if (closed || !stages.CURRICULUM_TOTAL.completedCount || stages.CURRICULUM_TOTAL.errorCount) return;
      end(begin("TABLE_READY"));
      stages.TABLE_READY.elapsedMs = elapsed(started);
      stages.TABLE_READY.maxElapsedMs = stages.TABLE_READY.elapsedMs;
      finish("ok");
    }) };
  } catch { return noop; }
}

// The original operation still runs if diagnostics are unavailable or fail.
export async function observeLessonLoad(diagnostic, stage, operation, queryResult = false) {
  let token;
  try { token = diagnostic?.begin(stage); } catch {}
  try {
    const value = await operation();
    try { diagnostic?.end(token, null, queryResult ? value.docs.length : 0); } catch {}
    return value;
  } catch (error) {
    try { diagnostic?.end(token, error); } catch {}
    throw error;
  }
}
